import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ThumbsUp, Edit2, Trash2, Reply, Send, X, MessageSquare, Loader2, PenLine, AlertTriangle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { projectBoardService } from '../api/boardApi';
import { toast } from 'react-hot-toast';
import { BRAND_COLORS, SYSTEM_COLORS, NEUTRAL_COLORS } from '../../../constants/brandPalette';

// Derive a stable accent color from a string (author name)
const AVATAR_PALETTE = [
  '#37559e', '#7c3aed', '#0891b2', '#059669',
  '#d97706', '#dc2626', '#db2777', '#65a30d',
];
const hashStr = (s = '') => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};
const avatarColor = (name) => AVATAR_PALETTE[hashStr(name) % AVATAR_PALETTE.length];
const initials = (name = '') =>
  name.split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?';

// Server returns naive UTC strings without Z — coerce to UTC before parsing
const parseUTC = (ts) => {
  if (!ts) return null;
  const s = String(ts);
  return new Date(/[Z+\-]\d{2}:?\d{2}$|Z$/.test(s) ? s : s + 'Z');
};

const relTime = (ts) => {
  if (!ts) return '';
  try { return formatDistanceToNow(parseUTC(ts), { addSuffix: true }); }
  catch { return ''; }
};

// ── QuoteBlock — the WhatsApp-style reply preview ────────────────────────────
const QuoteBlock = ({ comment, accentColor }) => (
  <div
    className="flex rounded-lg overflow-hidden mb-2"
    style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}
  >
    <div className="w-1 flex-shrink-0 rounded-l-lg" style={{ backgroundColor: accentColor }} />
    <div className="px-3 py-2 min-w-0">
      <p className="text-[11px] font-semibold mb-0.5 truncate" style={{ color: accentColor }}>
        {comment?.authorName || 'Unknown'}
      </p>
      <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
        {comment?.content || ''}
      </p>
    </div>
  </div>
);

// ── CommentCard ───────────────────────────────────────────────────────────────
const CommentCard = ({ comment, parentComment, currentUserId, onReply, onLike, onEdit, onDelete, hasRole }) => {
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const isOwn = comment.authorId === currentUserId;
  const canEdit  = isOwn || hasRole(['admin', 'supervisor']);
  const liked    = Array.isArray(comment.likes) && comment.likes.includes(currentUserId);
  const likeCount = Array.isArray(comment.likes) ? comment.likes.length : 0;
  const color = avatarColor(comment.authorName);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editContent.trim()) return;
    await onEdit(comment.id, editContent.trim());
    setEditing(false);
  };

  return (
    <div className="flex gap-3 group">
      {/* Avatar */}
      <div
        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white"
        style={{ backgroundColor: color, marginTop: 2 }}
      >
        {initials(comment.authorName)}
      </div>

      {/* Bubble */}
      <div className="flex-1 min-w-0">
        <div
          className="rounded-2xl rounded-tl-sm px-4 py-3"
          style={{
            backgroundColor: isOwn ? SYSTEM_COLORS.shellSurfaceMuted : SYSTEM_COLORS.shellSurface,
            border: `1px solid ${SYSTEM_COLORS.borderSoft}`,
          }}
        >
          {/* Header */}
          <div className="flex items-baseline justify-between gap-2 mb-1">
            <span className="text-[13px] font-semibold" style={{ color: color }}>
              {comment.authorName}
            </span>
            <span className="text-[10px] flex-shrink-0" style={{ color: SYSTEM_COLORS.textSoft }}>
              {relTime(comment.createdAt)}
            </span>
          </div>

          {/* Quote preview (reply) */}
          {parentComment && (
            <QuoteBlock comment={parentComment} accentColor={avatarColor(parentComment.authorName)} />
          )}

          {/* Content / edit form */}
          {editing ? (
            <form onSubmit={handleSave}>
              <textarea
                autoFocus
                value={editContent}
                onChange={e => setEditContent(e.target.value)}
                rows={3}
                className="w-full text-sm rounded-lg border px-3 py-2 outline-none resize-none focus:ring-2"
                style={{ borderColor: SYSTEM_COLORS.border, '--tw-ring-color': SYSTEM_COLORS.primaryRing }}
              />
              <div className="flex gap-2 mt-2">
                <button
                  type="submit"
                  disabled={!editContent.trim()}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg text-white disabled:opacity-50"
                  style={{ backgroundColor: BRAND_COLORS.primary }}
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => { setEditing(false); setEditContent(comment.content); }}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg"
                  style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted }}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <p className="text-[13.5px] leading-relaxed whitespace-pre-wrap break-words"
              style={{ color: NEUTRAL_COLORS.black }}>
              {comment.content}
            </p>
          )}
        </div>

        {/* Action row */}
        {!editing && (
          <div className="flex items-center gap-3 mt-1.5 px-1">
            <button
              onClick={() => onLike(comment.id)}
              className="flex items-center gap-1 text-[11px] font-medium transition-colors"
              style={{ color: liked ? BRAND_COLORS.primary : SYSTEM_COLORS.textSoft }}
            >
              <ThumbsUp size={12} fill={liked ? BRAND_COLORS.primary : 'none'} />
              {likeCount > 0 && <span>{likeCount}</span>}
            </button>
            <button
              onClick={() => onReply(comment)}
              className="flex items-center gap-1 text-[11px] font-medium transition-colors hover:text-gray-700"
              style={{ color: SYSTEM_COLORS.textSoft }}
            >
              <Reply size={12} />
              Reply
            </button>
            {canEdit && (
              <>
                <button
                  onClick={() => setEditing(true)}
                  className="flex items-center gap-1 text-[11px] font-medium opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ color: SYSTEM_COLORS.textSoft }}
                >
                  <Edit2 size={11} />
                </button>
                <button
                  onClick={() => onDelete(comment.id)}
                  className="flex items-center gap-1 text-[11px] font-medium opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ color: SYSTEM_COLORS.danger }}
                >
                  <Trash2 size={11} />
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Delete confirmation modal ─────────────────────────────────────────────────
const DeleteConfirmModal = ({ comment, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
    style={{ backgroundColor: 'rgba(15,23,42,0.45)' }}
    onClick={onCancel}
  >
    <div
      className="w-full max-w-sm rounded-2xl border p-6 shadow-xl"
      style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}
      onClick={e => e.stopPropagation()}
    >
      {/* Icon */}
      <div className="flex justify-center mb-4">
        <div className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{ backgroundColor: SYSTEM_COLORS.dangerTint }}>
          <AlertTriangle size={22} style={{ color: SYSTEM_COLORS.danger }} />
        </div>
      </div>

      {/* Text */}
      <h3 className="text-base font-semibold text-center mb-1" style={{ color: '#111827' }}>
        Delete comment?
      </h3>
      <p className="text-sm text-center mb-1 leading-relaxed" style={{ color: SYSTEM_COLORS.textMuted }}>
        This action cannot be undone.
      </p>
      {comment?.content && (
        <p className="text-xs text-center italic line-clamp-2 mt-2 mb-4 px-2"
          style={{ color: SYSTEM_COLORS.textSoft }}>
          "{comment.content}"
        </p>
      )}

      {/* Actions */}
      <div className="flex gap-3 mt-5">
        <button
          onClick={onCancel}
          className="flex-1 rounded-xl border py-2.5 text-sm font-semibold transition-colors hover:bg-gray-50"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, color: SYSTEM_COLORS.textMuted }}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="flex-1 rounded-xl py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          style={{ backgroundColor: SYSTEM_COLORS.danger }}
        >
          Delete
        </button>
      </div>
    </div>
  </div>
);

// ── Main Comments ─────────────────────────────────────────────────────────────
const Comments = ({ projectId }) => {
  const { user, hasRole } = useApiAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showFAB, setShowFAB] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const inputRef = useRef(null);
  const composeRef = useRef(null);
  const currentUserId = user?.id || user?.uid;

  // Show FAB when compose box scrolls out of view
  useEffect(() => {
    const el = composeRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowFAB(!entry.isIntersecting),
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading]);

  const scrollToCompose = () => {
    composeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    setTimeout(() => inputRef.current?.focus(), 380);
  };

  const load = useCallback(async () => {
    try {
      const data = await projectBoardService.getProjectComments(projectId);
      // Sort oldest-first so replies always appear after their parent
      setComments([...data].sort((a, b) => parseUTC(a.createdAt) - parseUTC(b.createdAt)));
    } catch {
      toast.error('Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const handleReply = (comment) => {
    setReplyTo(comment);
    composeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    setTimeout(() => inputRef.current?.focus(), 380);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || submitting) return;
    if (!currentUserId) { toast.error('Sign in to comment'); return; }
    setSubmitting(true);
    try {
      const created = await projectBoardService.addProjectComment(projectId, {
        content: newComment.trim(),
        parentId: replyTo?.id || null,
      });
      setComments(prev =>
        [...prev, created].sort((a, b) => parseUTC(a.createdAt) - parseUTC(b.createdAt))
      );
      setNewComment('');
      setReplyTo(null);
    } catch {
      toast.error('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (commentId, content) => {
    try {
      const updated = await projectBoardService.updateProjectComment(projectId, commentId, { content });
      setComments(prev => prev.map(c => c.id === commentId ? updated : c));
      toast.success('Comment updated');
    } catch {
      toast.error('Failed to update comment');
    }
  };

  const handleDelete = (commentId) => {
    const comment = comments.find(c => c.id === commentId);
    setDeleteTarget(comment || { id: commentId });
  };

  const confirmDelete = async () => {
    const commentId = deleteTarget?.id;
    setDeleteTarget(null);
    // Optimistic remove
    setComments(prev => prev.filter(c => c.id !== commentId));
    try {
      await projectBoardService.deleteProjectComment(projectId, commentId);
      toast.success('Comment deleted');
    } catch {
      // Rollback on failure
      load();
      toast.error('Failed to delete comment');
    }
  };

  const handleLike = async (commentId) => {
    if (!currentUserId) { toast.error('Sign in to like comments'); return; }
    try {
      const updated = await projectBoardService.toggleCommentLike(projectId, commentId);
      setComments(prev => prev.map(c => c.id === commentId ? updated : c));
    } catch {
      toast.error('Failed to update like');
    }
  };

  const byId = Object.fromEntries(comments.map(c => [c.id, c]));

  return (
    <div className="flex flex-col gap-4">
      {deleteTarget && (
        <DeleteConfirmModal
          comment={deleteTarget}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
      <div className="flex items-center gap-2">
        <MessageSquare size={16} style={{ color: BRAND_COLORS.primary }} />
        <h3 className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>
          Comments
          {comments.length > 0 && (
            <span className="ml-1.5 text-xs font-normal" style={{ color: SYSTEM_COLORS.textSoft }}>
              ({comments.length})
            </span>
          )}
        </h3>
      </div>

      {/* Thread */}
      {loading ? (
        <div className="flex items-center justify-center py-10 gap-2">
          <Loader2 size={16} className="animate-spin" style={{ color: BRAND_COLORS.primary }} />
          <span className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>Loading…</span>
        </div>
      ) : comments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 gap-2">
          <MessageSquare size={24} style={{ color: SYSTEM_COLORS.border }} />
          <p className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>No comments yet. Start the conversation.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {comments.map(comment => (
            <CommentCard
              key={comment.id}
              comment={comment}
              parentComment={comment.parentId ? byId[comment.parentId] : null}
              currentUserId={currentUserId}
              hasRole={hasRole}
              onReply={handleReply}
              onLike={handleLike}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Compose box */}
      <div
        ref={composeRef}
        className="rounded-2xl border p-3"
        style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}
      >
        {/* Reply banner */}
        {replyTo && (
          <div
            className="flex items-start gap-2 rounded-xl p-2 mb-2"
            style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}
          >
            <div className="w-0.5 self-stretch rounded-full flex-shrink-0" style={{ backgroundColor: avatarColor(replyTo.authorName) }} />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold mb-0.5" style={{ color: avatarColor(replyTo.authorName) }}>
                {replyTo.authorName}
              </p>
              <p className="text-xs truncate" style={{ color: SYSTEM_COLORS.textMuted }}>{replyTo.content}</p>
            </div>
            <button onClick={() => setReplyTo(null)} className="flex-shrink-0 mt-0.5">
              <X size={13} style={{ color: SYSTEM_COLORS.textSoft }} />
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex gap-2 items-end">
          {/* Current user avatar */}
          <div
            className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white mb-0.5"
            style={{ backgroundColor: avatarColor(user?.name || user?.fullName || '') }}
          >
            {initials(user?.name || user?.fullName || user?.email || '')}
          </div>

          <textarea
            ref={inputRef}
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); }
            }}
            placeholder={replyTo ? `Reply to ${replyTo.authorName}…` : 'Write a comment… (Enter to send)'}
            rows={1}
            className="flex-1 text-sm bg-transparent resize-none outline-none leading-relaxed placeholder:text-gray-400"
            style={{
              minHeight: 36,
              maxHeight: 120,
              color: NEUTRAL_COLORS.black,
            }}
          />
          <button
            type="submit"
            disabled={!newComment.trim() || submitting}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-opacity disabled:opacity-40"
            style={{ backgroundColor: BRAND_COLORS.primary }}
          >
            {submitting
              ? <Loader2 size={14} className="animate-spin text-white" />
              : <Send size={14} className="text-white" />}
          </button>
        </form>
      </div>

      {/* Floating action button — visible when compose box is scrolled out of view */}
      <div
        className="sticky bottom-4 flex justify-end pointer-events-none"
        style={{ marginTop: -8 }}
      >
        <button
          onClick={scrollToCompose}
          title="Add a comment"
          className="pointer-events-auto flex items-center gap-2 rounded-full pl-4 pr-3.5 py-2.5 text-xs font-semibold text-white shadow-lg transition-all duration-200"
          style={{
            backgroundColor: BRAND_COLORS.primary,
            opacity: showFAB ? 1 : 0,
            transform: showFAB ? 'translateY(0) scale(1)' : 'translateY(8px) scale(0.92)',
            pointerEvents: showFAB ? 'auto' : 'none',
            boxShadow: '0 4px 20px rgba(55,85,158,0.35)',
          }}
        >
          <PenLine size={13} />
          Comment
        </button>
      </div>
    </div>
  );
};

export default Comments;
